import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {
  cancelBonusQuestion,
  fetchChampionshipCalendarGameWeeks,
  fetchBonusQuestions,
} from '../api';
import ConfirmModal from '../components/ConfirmModal';
import {
  BONUS_QUESTION_ACTIONS_BY_STATUS,
  BONUS_QUESTION_ACTION_ICONS,
  BONUS_QUESTION_ACTION_LABELS,
  BONUS_QUESTION_STATUS_LABELS,
  DEFAULT_CHAMPIONSHIP_ID,
  QUESTION_TYPE_LABELS,
  questionBonusEditPath,
  questionBonusResolvePath,
  questionBonusViewPath,
} from '../constants';
import {listStyles, questionBonusStyles} from '../styles';
import moment from 'moment';
import {computeBonusQuestionStatus} from '../status';
import {BonusQuestionAction, BonusQuestionListItem} from '../types';
import {indexCalendarGameWeeksByNumber} from '../utils';

const COLUMNS = [
  {key: 'gameWeek', label: 'Journée', width: '9%'},
  {key: 'questionType', label: 'Format', width: '9%'},
  {key: 'deadline', label: 'Deadline', width: '12%'},
  {key: 'title', label: 'Question', width: '26%'},
  {key: 'status', label: 'Statut', width: '13%'},
  {key: 'visibility', label: 'Visibilité', width: '11%'},
  {key: 'actions', label: 'Actions', width: '20%'},
];

const ActionButton = ({
  action,
  onClick,
}: {
  action: BonusQuestionAction;
  onClick: () => void;
}) => {
  const label = BONUS_QUESTION_ACTION_LABELS[action];
  const icon = BONUS_QUESTION_ACTION_ICONS[action];

  if (!icon) {
    return (
      <button
        type="button"
        title={label}
        onClick={onClick}
        style={listStyles.resolveAction}>
        {label}
      </button>
    );
  }

  const isDestructive = action === 'delete' || action === 'cancel';

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      style={{
        ...listStyles.iconAction,
        ...(isDestructive ? listStyles.iconActionDanger : {}),
      }}>
      <i className={icon} />
    </button>
  );
};

interface QuestionBonusListProps {
  history?: {push: (path: string) => void};
}

export const QuestionBonusList = ({history}: QuestionBonusListProps) => {
  const dispatch = useDispatch();
  const [questions, setQuestions] = useState<BonusQuestionListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [questionToCancel, setQuestionToCancel] = useState<
    BonusQuestionListItem | undefined
  >(undefined);
  const [isCancelling, setIsCancelling] = useState(false);
  const [gameWeeksByNumber, setGameWeeksByNumber] = useState<
    Record<number, {startDate: string; endDate: string}>
  >({});

  useEffect(() => {
    dispatch(
      fetchChampionshipCalendarGameWeeks(
        DEFAULT_CHAMPIONSHIP_ID,
        gameWeeks =>
          setGameWeeksByNumber(indexCalendarGameWeeksByNumber(gameWeeks)),
        () => setGameWeeksByNumber({}),
      ),
    );
  }, [dispatch]);

  const loadQuestions = useCallback(() => {
    setIsLoading(true);
    setError(undefined);
    dispatch(
      fetchBonusQuestions(
        DEFAULT_CHAMPIONSHIP_ID,
        fetchedQuestions => {
          setQuestions(fetchedQuestions);
          setIsLoading(false);
        },
        () => {
          setQuestions([]);
          setError('Le chargement des questions a échoué.');
          setIsLoading(false);
        },
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  /** "Supprimer" et "Annuler" déclenchent le même PATCH /cancel. */
  const confirmCancel = () => {
    if (!questionToCancel) return;
    setIsCancelling(true);
    dispatch(
      cancelBonusQuestion(
        questionToCancel.id,
        () => {
          setIsCancelling(false);
          setQuestionToCancel(undefined);
          loadQuestions();
        },
        () => {
          setIsCancelling(false);
          setQuestionToCancel(undefined);
          setError("L'annulation de la question a échoué.");
        },
      ),
    );
  };

  const runAction = (
    action: BonusQuestionAction,
    question: BonusQuestionListItem,
  ) => {
    if (action === 'view') {
      if (history) history.push(questionBonusViewPath(question.id));
      return;
    }
    if (action === 'edit') {
      if (history) history.push(questionBonusEditPath(question.id));
      return;
    }
    if (action === 'resolve') {
      if (history) history.push(questionBonusResolvePath(question.id));
      return;
    }
    if (action === 'delete' || action === 'cancel') {
      setQuestionToCancel(question);
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={COLUMNS.length} style={listStyles.stateMessage}>
            Chargement des questions…
          </td>
        </tr>
      );
    }

    if (error) {
      return (
        <tr>
          <td
            colSpan={COLUMNS.length}
            style={{
              ...listStyles.stateMessage,
              ...questionBonusStyles.errorMessage,
            }}>
            {error}
          </td>
        </tr>
      );
    }

    if (questions.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMNS.length} style={listStyles.stateMessage}>
            Aucune question bonus pour le moment.
          </td>
        </tr>
      );
    }

    const now = moment();

    return questions.map((question, index) => {
      const gameWeek = question.gameWeekNumber
        ? gameWeeksByNumber[question.gameWeekNumber]
        : undefined;
      const status = computeBonusQuestionStatus(question, gameWeek, now);

      return (
        <tr
          key={question.id}
          style={index % 2 === 0 ? undefined : listStyles.rowOdd}>
          <td style={listStyles.cell}>
            {question.gameWeekNumber ? `J${question.gameWeekNumber}` : '-'}
          </td>
          <td style={listStyles.cell}>
            {QUESTION_TYPE_LABELS[question.questionType]}
          </td>
          <td style={listStyles.cell}>
            {gameWeek?.startDate
              ? moment(gameWeek.startDate).format('DD/MM HH:mm')
              : '-'}
          </td>
          <td style={{...listStyles.cell, ...listStyles.titleCell}}>
            {question.title?.['fr-FR'] || '-'}
          </td>
          <td style={listStyles.cell}>
            {BONUS_QUESTION_STATUS_LABELS[status]}
          </td>
          <td style={listStyles.cell}>
            {question.visibilityHoursBeforeGameWeekStart
              ? `${question.visibilityHoursBeforeGameWeekStart}h`
              : '-'}
          </td>
          <td style={listStyles.cell}>
            <div style={listStyles.actionsCell}>
              {BONUS_QUESTION_ACTIONS_BY_STATUS[status].map(action => (
                <ActionButton
                  key={action}
                  action={action}
                  onClick={() => runAction(action, question)}
                />
              ))}
            </div>
          </td>
        </tr>
      );
    });
  };

  return (
    <div style={questionBonusStyles.page}>
      <h1 style={questionBonusStyles.pageTitle}>
        List of questions
        <span style={questionBonusStyles.pageTitleSuffix}>MPP</span>
      </h1>

      <div style={listStyles.card}>
        <table style={listStyles.table}>
          <thead>
            <tr>
              {COLUMNS.map(column => (
                <th
                  key={column.key}
                  style={{
                    ...listStyles.headerCell,
                    width: column.width,
                    textAlign: column.key === 'actions' ? 'right' : 'left',
                  }}>
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderBody()}</tbody>
        </table>
      </div>

      {questionToCancel && (
        <ConfirmModal
          variant="danger"
          title="Etes-vous sûr ?"
          description="Une fois la question annulée, la card de la question passera au statut correspondant sur l'app et aucun utilisateur ne gagnera de point"
          cancelLabel="Revenir en arrière"
          confirmLabel="Annuler la question"
          isConfirming={isCancelling}
          onConfirm={confirmCancel}
          onCancel={() => setQuestionToCancel(undefined)}
        />
      )}
    </div>
  );
};

export default QuestionBonusList;



// WEBPACK FOOTER //
// ./src/pages/Other/MPP/QuestionBonus/List/index.tsx